import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import {
    Add,
    Chart,
    Copy,
    DocumentText,
    Edit2,
    Eye,
    MagicStar,
    Pause,
    Play,
    Profile2User,
    Send2,
    Trash,
} from "iconsax-react";
import RowActionButton from "./RowActionButton";
import CampaignSetupModal from "./CampaignSetupModal";
import SurveyGuideModal from "./SurveyGuideModal";
import MainPanelHeader from "../../layouts/mainPanelHeader";
import DBButton from "../../component/global/DBButton";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBTable from "../../component/global/DBTable/DBTable";
import DBPagination from "../../component/global/DBPagination";
import DBSpinner from "../../component/global/DBSpinner";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SURVEY_LIST,
    surveyActionUrl,
} from "../../helperData/ApiAddress";
import {
    SURVEY_STATUS,
    formatSurveyDateTime,
    surveyChannelLabel,
    surveyStatusLabel,
    surveyStatusOptions,
    surveySubjectLabel,
    withPersonalScope,
    SURVEY_CHANNEL,
} from "./constants";
import SurveyFormModal from "./SurveyFormModal";
import SurveyFeaturesModal from "./SurveyFeaturesModal";
import TemplateSelectModal from "./TemplateSelectModal";
import DuplicateSelectModal from "./DuplicateSelectModal";
import SurveyDetail from "./SurveyDetail";
import CampaignsPanel from "./CampaignsPanel";
import SettingsPanel from "./SettingsPanel";
import SettingsHelpModal from "./SettingsHelpModal";
import SurveyPreviewModal from "./SurveyPreviewModal";
import AIFormModal from "../exp-settings/components/forms-tab/AIFormModal";
import { translateServerDetail } from "./translateServerDetail";
import "./style.scss";

export default function Surveys({ personal = false, embedded = false }) {
    const toast = useContext(ToastContext);
    const [selectedTab, setSelectedTab] = useState("surveys");

    const [surveys, setSurveys] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);

    const [formModal, setFormModal] = useState({
        show: false,
        survey: null,
        initialStructure: null,
        initialForm: null,
    });
    const [templateModalShow, setTemplateModalShow] = useState(false);
    const [duplicateModalShow, setDuplicateModalShow] = useState(false);
    const [aiModalShow, setAiModalShow] = useState(false);
    const [managedSurvey, setManagedSurvey] = useState(null);
    const [confirmState, setConfirmState] = useState(null);
    const [settingsHelpShow, setSettingsHelpShow] = useState(false);
    const [guideShow, setGuideShow] = useState(false);
    const [campaignSetup, setCampaignSetup] = useState(null);
    const [wizardStartAtFields, setWizardStartAtFields] = useState(false);
    const [featuresSurvey, setFeaturesSurvey] = useState(null);
    const [previewSurveyId, setPreviewSurveyId] = useState(null);

    const [getSurveys, getSurveysLoading] = useRequest(SURVEY_LIST, "GET");
    const [actionLoading, setActionLoading] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    const loadSurveys = (page = currentPage) => {
        getSurveys(
            withPersonalScope(personal, {
                page,
                search: search || undefined,
                status: statusFilter || undefined,
            })
        )
            .then((r) => {
                setSurveys(r.results || []);
                setTotalCount(r.count || 0);
            })
            .catch(() => toast(t("survey.load.error"), "danger"));
    };

    useEffect(() => {
        loadSurveys(1);
        setCurrentPage(1);
    }, [search, statusFilter]);

    useEffect(() => {
        loadSurveys(currentPage);
    }, [currentPage]);

    const handleStatusConfirm = () => {
        const { survey, action } = confirmState;
        setActionLoading(true);
        axios
            .post(surveyActionUrl(survey.id, action), {}, {
                headers: authHeaders(),
            })
            .then(() => {
                toast(t("survey.status.change.success"), "success");
                setConfirmState(null);
                loadSurveys();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(
                    detail || t("survey.status.change.error"),
                    "danger"
                );
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const handleArchive = () => {
        const { survey } = confirmState;
        setActionLoading(true);
        axios
            .delete(`${SURVEY_LIST}${survey.id}/`, { headers: authHeaders() })
            .then(() => {
                toast(t("survey.status.change.success"), "success");
                setConfirmState(null);
                loadSurveys();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(
                    detail || t("survey.status.change.error"),
                    "danger"
                );
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const handleDuplicate = (survey) => {
        axios
            .post(surveyActionUrl(survey.id, "duplicate"), {}, {
                headers: authHeaders(),
            })
            .then(() => {
                toast(t("survey.duplicate.success"), "success");
                loadSurveys();
            })
            .catch(() => toast(t("survey.duplicate.error"), "danger"));
    };

    const confirmCopy = {
        open: {
            title: t("survey.open.confirm.title"),
            description: t("survey.open.confirm.description"),
        },
        close: {
            title: t("survey.close.confirm.title"),
            description: t("survey.close.confirm.description"),
        },
        archive: {
            title: t("survey.delete.confirm.title"),
            description: t("survey.delete.confirm.description"),
        },
    };

    const headers = [
        {
            id: "title",
            title: t("survey.table.title"),
            gridTemplateColumns: "minmax(120px, 2fr)",
        },
        {
            id: "subject",
            title: t("survey.table.subject"),
            gridTemplateColumns: "minmax(0, 1.4fr)",
        },
        {
            id: "status",
            title: t("survey.table.status"),
            gridTemplateColumns: "minmax(72px, 0.8fr)",
        },
        {
            id: "channel",
            title: t("survey.table.channel"),
            gridTemplateColumns: "minmax(88px, 0.9fr)",
        },
        {
            id: "responses",
            title: t("survey.table.responses"),
            gridTemplateColumns: "minmax(48px, 0.7fr)",
        },
        {
            id: "created",
            title: t("survey.table.created"),
            gridTemplateColumns: "minmax(148px, max-content)",
        },
        {
            id: "actions",
            title: t("survey.table.actions"),
            gridTemplateColumns: "minmax(240px, max-content)",
        },
    ];

    const rows = surveys.map((survey) => {
        const subject = surveySubjectLabel(survey);
        return {
            title: (
            <div className="survey-title-cell">
                <span className="survey-title-text">{survey.title}</span>
                {subject && subject !== survey.title ? (
                    <span className="survey-title-subject">{subject}</span>
                ) : null}
            </div>
        ),
        subject,
        status: (
            <span className={`survey-status status-${survey.status}`}>
                {surveyStatusLabel(survey.status)}
            </span>
        ),
        channel: (
            <span
                className={`survey-channel-badge channel-${
                    survey.channel || SURVEY_CHANNEL.WEB
                }`}
            >
                {surveyChannelLabel(survey.channel || SURVEY_CHANNEL.WEB)}
            </span>
        ),
        responses: survey.response_count,
        created: (
            <span className="survey-created-at" translate="no">
                {formatSurveyDateTime(survey.jalali_created_at)}
            </span>
        ),
        actions: (
            <div
                className="survey-row-actions"
                onClick={(e) => e.stopPropagation()}
            >
                {survey.status !== SURVEY_STATUS.ARCHIVED && (
                    <RowActionButton
                        icon={Send2}
                        label={t("survey.campaign.action.send")}
                        onClick={() => {
                            setWizardStartAtFields(false);
                            setCampaignSetup(survey);
                        }}
                    />
                )}
                <RowActionButton
                    icon={Chart}
                    label={t("survey.detail.results")}
                    onClick={() => setManagedSurvey(survey)}
                />
                {survey.status !== SURVEY_STATUS.ARCHIVED && (
                    <RowActionButton
                        icon={Profile2User}
                        label={t("survey.action.features")}
                        onClick={() => setFeaturesSurvey(survey)}
                    />
                )}
                {survey.status !== SURVEY_STATUS.ARCHIVED && (
                    <RowActionButton
                        icon={Edit2}
                        label={t("survey.action.edit")}
                        onClick={() =>
                            setFormModal({ show: true, survey })
                        }
                    />
                )}
                <RowActionButton
                    icon={Eye}
                    label={t("survey.action.preview")}
                    onClick={() => setPreviewSurveyId(survey.id)}
                />
                {(survey.status === SURVEY_STATUS.DRAFT ||
                    survey.status === SURVEY_STATUS.CLOSED) && (
                    <RowActionButton
                        icon={Play}
                        label={t("survey.action.open")}
                        onClick={() =>
                            setConfirmState({ survey, action: "open" })
                        }
                    />
                )}
                {survey.status === SURVEY_STATUS.OPEN && (
                    <RowActionButton
                        icon={Pause}
                        label={t("survey.action.close")}
                        onClick={() =>
                            setConfirmState({ survey, action: "close" })
                        }
                    />
                )}
                {survey.status !== SURVEY_STATUS.ARCHIVED && (
                    <>
                        <RowActionButton
                            icon={Copy}
                            label={t("survey.action.duplicate")}
                            onClick={() => handleDuplicate(survey)}
                        />
                        <RowActionButton
                            icon={Trash}
                            danger
                            label={t("survey.action.delete")}
                            onClick={() =>
                                setConfirmState({
                                    survey,
                                    action: "archive",
                                })
                            }
                        />
                    </>
                )}
            </div>
        ),
    };
    });

    if (managedSurvey) {
        return (
            <SurveyDetail
                surveyId={managedSurvey.id}
                personal={personal}
                embedded={embedded}
                initialTab={managedSurvey.initialTab || "results"}
                initialPhoneCampaignId={managedSurvey.phoneCampaignId}
                onBack={() => {
                    setManagedSurvey(null);
                    loadSurveys();
                }}
            />
        );
    }

    const surveysHelpTitle =
        selectedTab === "settings"
            ? t("survey.settings.help.title")
            : t("survey.guide.title");

    const surveysList = (
        <div className="surveys-content">
            <div className="surveys-toolbar">
                <DBInput
                    size="large"
                    value={search}
                    placeholder={t("survey.search.placeholder")}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <DBSelect
                    size="large"
                    allowClear
                    placeholder={t("survey.filter.status")}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value ?? null)}
                    options={surveyStatusOptions()}
                />
                <div className="surveys-create-actions">
                    <DBButton
                        caption={
                            <>
                                <Add size={18} /> {t("survey.create.blank")}
                            </>
                        }
                        onClick={() =>
                            setFormModal({
                                show: true,
                                survey: null,
                            })
                        }
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        caption={
                            <>
                                <DocumentText size={18} />{" "}
                                {t("survey.create.template")}
                            </>
                        }
                        onClick={() => setTemplateModalShow(true)}
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        caption={
                            <>
                                <Copy size={18} />{" "}
                                {t("survey.create.duplicate")}
                            </>
                        }
                        onClick={() => setDuplicateModalShow(true)}
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        caption={
                            <>
                                <MagicStar size={18} /> {t("survey.create.ai")}
                            </>
                        }
                        onClick={() => setAiModalShow(true)}
                    />
                </div>
            </div>
            {getSurveysLoading ? (
                <DBSpinner tip={t("loading")} />
            ) : surveys.length === 0 ? (
                <div className="surveys-empty">{t("survey.list.empty")}</div>
            ) : (
                <>
                    <div className="surveys-table-wrap surveys-list-table">
                        <DBTable
                            minWidth={900}
                            headers={headers}
                            rows={rows}
                        />
                    </div>
                    <DBPagination
                        current={currentPage}
                        total={totalCount}
                        onChange={(page) => setCurrentPage(page)}
                    />
                </>
            )}
        </div>
    );

    return (
        <div
            className={[
                "surveys-page",
                embedded ? "is-embedded" : "main_panel_box",
                personal ? "is-personal" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {!embedded && (
                <MainPanelHeader
                    panelType={t("Corpration.Panel.label")}
                    panelName={t("survey.title")}
                />
            )}
            <div className={embedded ? undefined : "main_panel_content"}>
                <div className={embedded ? undefined : "right_content main_box"}>
                    {personal ? (
                        <div className="surveys-personal-wrap">
                            <button
                                type="button"
                                className="surveys-tab-help"
                                title={t("survey.guide.title")}
                                aria-label={t("survey.guide.title")}
                                onClick={() => setGuideShow(true)}
                            >
                                ?
                            </button>
                            {surveysList}
                        </div>
                    ) : (
                    <div className="tab-section surveys-tab-section has-tab-help">
                        <button
                            type="button"
                            className="surveys-tab-help"
                            title={surveysHelpTitle}
                            aria-label={surveysHelpTitle}
                            onClick={() =>
                                selectedTab === "settings"
                                    ? setSettingsHelpShow(true)
                                    : setGuideShow(true)
                            }
                        >
                            ?
                        </button>
                        <Tabs
                            activeKey={selectedTab}
                            transition={false}
                            mountOnEnter
                            unmountOnExit
                            className="panel_tab"
                            onSelect={(key) => setSelectedTab(key || "surveys")}
                        >
                            <Tab
                                eventKey="surveys"
                                className="panel_tab_item"
                                title={t("survey.tab.surveys")}
                            >
                                {surveysList}
                            </Tab>
                            <Tab
                                eventKey="campaigns"
                                className="panel_tab_item"
                                title={t("survey.tab.campaigns")}
                            >
                                <div className="surveys-content">
                                    <CampaignsPanel
                                        onOpenCallCenter={(campaign) => {
                                            if (!campaign?.survey) return;
                                            setManagedSurvey({
                                                id: campaign.survey,
                                                initialTab: "phone",
                                                phoneCampaignId: campaign.id,
                                            });
                                        }}
                                    />
                                </div>
                            </Tab>
                            <Tab
                                eventKey="settings"
                                className="panel_tab_item"
                                title={t("survey.tab.settings")}
                            >
                                <div className="surveys-content">
                                    <SettingsPanel />
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                    )}
                </div>
            </div>

            <SurveyFormModal
                show={formModal.show}
                survey={formModal.survey}
                personal={personal}
                initialStructure={formModal.initialStructure}
                initialForm={formModal.initialForm}
                onHide={() =>
                    setFormModal({
                        show: false,
                        survey: null,
                        initialStructure: null,
                        initialForm: null,
                    })
                }
                onSuccess={(saved, options = {}) => {
                    setFormModal({
                        show: false,
                        survey: null,
                        initialStructure: null,
                        initialForm: null,
                    });
                    loadSurveys();
                    if (options.isCreate && saved?.id) {
                        setWizardStartAtFields(true);
                        setCampaignSetup(saved);
                    }
                }}
            />
            <TemplateSelectModal
                show={templateModalShow}
                personal={personal}
                onHide={() => setTemplateModalShow(false)}
                onSuccess={(saved, options = {}) => {
                    setTemplateModalShow(false);
                    loadSurveys();
                    if (options.isCreate && saved?.id) {
                        setWizardStartAtFields(true);
                        setCampaignSetup(saved);
                    }
                }}
            />
            <DuplicateSelectModal
                show={duplicateModalShow}
                personal={personal}
                onHide={() => setDuplicateModalShow(false)}
                onSuccess={(saved, options = {}) => {
                    setDuplicateModalShow(false);
                    loadSurveys();
                    if (options.isCreate && saved?.id) {
                        setWizardStartAtFields(true);
                        setCampaignSetup(saved);
                    }
                }}
            />
            <AIFormModal
                show={aiModalShow}
                onHide={() => setAiModalShow(false)}
                variant="survey"
                personal={personal}
                onApplyStructure={({ structure, surveyForm }) => {
                    setAiModalShow(false);
                    setFormModal({
                        show: true,
                        survey: null,
                        initialStructure: structure,
                        initialForm: surveyForm,
                    });
                }}
            />
            <SettingsHelpModal
                show={settingsHelpShow}
                onHide={() => setSettingsHelpShow(false)}
            />
            <SurveyGuideModal
                show={guideShow}
                onHide={() => setGuideShow(false)}
            />
            <CampaignSetupModal
                show={!!campaignSetup}
                survey={campaignSetup}
                startAtFields={wizardStartAtFields}
                linkOnly={personal}
                onHide={() => {
                    setCampaignSetup(null);
                    setWizardStartAtFields(false);
                }}
                onSurveyChange={(next) => setCampaignSetup(next)}
            />
            <SurveyFeaturesModal
                show={!!featuresSurvey}
                survey={featuresSurvey}
                onHide={() => setFeaturesSurvey(null)}
                onSuccess={(saved) => {
                    setFeaturesSurvey(null);
                    if (saved?.id) loadSurveys();
                }}
            />
            <SurveyPreviewModal
                show={!!previewSurveyId}
                surveyId={previewSurveyId}
                onHide={() => setPreviewSurveyId(null)}
            />
            <DBConfirmModal
                show={!!confirmState}
                onHide={() => setConfirmState(null)}
                title={confirmCopy[confirmState?.action]?.title}
                description={confirmCopy[confirmState?.action]?.description}
                btnTitle={
                    confirmState?.action === "archive"
                        ? t("survey.action.delete")
                        : t("save")
                }
                loading={actionLoading}
                onConfirm={() => {
                    if (confirmState?.action === "archive") {
                        handleArchive();
                    } else {
                        handleStatusConfirm();
                    }
                }}
            />
        </div>
    );
}
